import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAdsConfig, getAdsGameState, getGameView, getGameTab } from '@gameday/utils/selectors/getAdsConfig';
import GamedayAdsEngine from './GamedayAdsEngine';
import useAds from '@gameday/utils/hooks/useAds';
import usePermissionToTrack from '@gameday/utils/hooks/usePermissionToTrack';
import Visibly from '@bower_components/bam-visibly/visibly';

const MIDINNING_AD_REFRESH_COUNT = 1;

class MLBGamedayAdsEngine extends GamedayAdsEngine {
    constructor (config) {
        const doubleClickMethodMap = {
            initDoubleclick: (config) => {
                this.doubleclickInit(config.pageUnits, config.doubleClickConfigObj);
            },
            setPageAdTargeting: (config) => {
                this.setPageAdTargeting(config);
            },
            doubleclickDisplay: (ad) => {
                this.display(ad);
            },
            doubleclickRefresh: (ad) => {
                this.refresh(ad);
            },
            doubleclickDefineSlot: (ad) => {
                this.defineSlot(ad);
            },
            doubleclickDestroySlots: (ads) => {
                this.destroySlots(ads);
            },
            drawSingleAd: (ad) => {
                this.drawSingleAd(ad);
            },
            doubleclickGetGptadslots: (ads) => {
                this.getGptadslots();
            },
            doubleclickListener: (event, cb) => {
                this.doubleclickListener(event, cb);
            },
            startRefreshTimer: () => {
                this.startRefreshTimer();
            },
            stopRefreshTimer: () => {
                this.stopRefreshTimer();
            },
            startRefreshMidInningAdTimer: (ad) => {
                this.startRefreshMidInningAdTimer(ad);
            },
            stopRefreshMidInningAdTimer: () => {
                this.stopRefreshMidInningAdTimer();
            },
            refreshAds: () => {
                this.refreshAds();
            }
        };
        const options = {
            config,
            doubleClickMethodMap
        };

        super(options);
        this.adEngine = new GamedayAdsEngine(options);
        this.midInningAdRefreshCount = MIDINNING_AD_REFRESH_COUNT;
    }

    doubleclickInit (pageUnits, doubleClickConfigObj) {
        let i = 0;
        let pageUnit;
        const len = pageUnits.length;
        doubleClickConfigObj.ads = [];

        if (this.resetResponsiveSlots) {
            this.resetResponsiveSlots();
        }

        for (; i < len; i++) {
            pageUnit = pageUnits[i];
            doubleClickConfigObj.ads.push(pageUnit);
        }

        this.init(doubleClickConfigObj);
    }

    drawSingleAd (ad) {
        const { container } = ad;
        if (document.getElementById(container)) {
            this.doubleclick.doubleclickDefineSlot(ad);
            this.doubleclick.doubleclickDisplay(container);
            this.doubleclick.doubleclickRefresh(container);
        }
    }

    init (props) {
        const defaultMethod = () => console.log('No Doubleclick init method found.', props);
        const init = (typeof window !== 'undefined' && window.mlbDoubleclick && window?.mlbDoubleclick?.init) ? window?.mlbDoubleclick?.init : defaultMethod;
        if (typeof props !== 'undefined') {
            init(props);
        }
    }

    defineSlot (props) {
        const defaultMethod = () => console.log('No Doubleclick defineSlot method found.', props);
        const defineSlot = (typeof window !== 'undefined' && window.mlbDoubleclick && window?.mlbDoubleclick?.defineSlot) ? window?.mlbDoubleclick?.defineSlot : defaultMethod;
        defineSlot(props);
    }

    destroySlots (props) {
        const defaultMethod = () => console.log('No Doubleclick destroySlots method found.', props);
        const destroySlots = (typeof window !== 'undefined' && window.mlbDoubleclick && window?.mlbDoubleclick?.destroySlots) ? window?.mlbDoubleclick?.destroySlots : defaultMethod;
        destroySlots(props);
    }

    display (props) {
        const defaultMethod = () => console.log('No Doubleclick display method found.', props);
        const display = (typeof window !== 'undefined' && window.mlbDoubleclick && window?.mlbDoubleclick?.display) ? window?.mlbDoubleclick?.display : defaultMethod;
        display(props);
    }

    refresh (props) {
        const defaultMethod = () => console.log('No Doubleclick refresh method found.', props);
        const refresh = (typeof window !== 'undefined' && window.mlbDoubleclick && window?.mlbDoubleclick?.refresh) ? window?.mlbDoubleclick?.refresh : defaultMethod;
        refresh(props);
    }

    resetResponsiveSlots () {
        const defaultMethod = () => console.log('No Doubleclick resetResponsiveSlots method found.');
        const resetResponsiveSlots = (typeof window !== 'undefined' && window.mlbDoubleclick && window?.mlbDoubleclick?.resetResponsiveSlots) ? window?.mlbDoubleclick?.resetResponsiveSlots : defaultMethod;
        resetResponsiveSlots();
    }

    setPageAdTargeting (props) {
        const defaultMethod = () => console.log('No Doubleclick setPageTargeting method found.', props);
        const setPageAdTargeting = (typeof window !== 'undefined' && window.mlbDoubleclick && window?.mlbDoubleclick?.setPageTargeting) ? window?.mlbDoubleclick?.setPageTargeting : defaultMethod;
        setPageAdTargeting(props);
    }

    getGptadslots () {
        return window?.gptadslots || {};
    }

    doubleclickListener (event, cb) {
        const defaultMethod = () => console.log('No Doubleclick.on method found.', event, cb);
        const doubleclickListener = (typeof window !== 'undefined' && window.mlbDoubleclick && window?.mlbDoubleclick?.on) ? window?.mlbDoubleclick?.on : defaultMethod;
        doubleclickListener(event, cb);
    }

    startRefreshTimer () {
        const that = this;
        this.refreshInterval = setInterval(function () {
            that.adEngine.doubleclick.refreshAds();
        }, this.adEngine.refreshRate);
    }

    stopRefreshTimer () {
        this.refreshInterval = false;
    }

    startRefreshMidInningAdTimer (ad) {
        const that = this;
        const { container } = ad;
        this.refreshMidInningAdInterval = setInterval(function () {
            const adDom = window?.document?.querySelector('#' + container);
            if (adDom) {
                that.midInningAdRefreshCount++;
                const cloned = { ...ad };
                cloned.targeting = { pos: that.midInningAdRefreshCount };
                that.destroySlots([container]);
                that.drawSingleAd(cloned);
            }
        }, this.adEngine.refreshRate);
    }

    stopRefreshMidInningAdTimer () {
        clearTimeout(this.refreshMidInningAdInterval);
        this.refreshMidInningAdInterval = false;
        this.midInningAdRefreshCount = MIDINNING_AD_REFRESH_COUNT;
    }

    refreshAds () {
        const that = this;
        this.refreshAdList.forEach((container) => {
            const inDom = window?.document?.querySelectorAll('#' + container)?.length > 0;
            if (inDom) {
                that.adEngine.doubleclick.doubleclickRefresh(container);
            }
        });
    }
};

export default () => {
    const AdsInfo = useAds();
    const { adsConfig } = useSelector(getAdsConfig);
    const { gameView } = useSelector(getGameView);
    const { gameTab } = useSelector(getGameTab);
    const GameData = useSelector(getAdsGameState);
    const [hasAds, setHasAds] = useState(false);
    const permissionToTrack = usePermissionToTrack();

    useEffect(() => {
        if (!window?.visibly && Visibly) {
            window.visibly = Visibly;
        }
    }, [Visibly]);

    useEffect(() => {
        if (typeof adsConfig !== 'undefined') {
            setHasAds(true);
            AdsInfo.setInstance(new MLBGamedayAdsEngine(adsConfig));
        }
    }, [adsConfig]);

    useEffect(() => {
        // permission changes, dont set it when undefined (app-mlb)
        const hasPubads = !!window?.googletag?.pubads;
        if (hasPubads && hasAds && typeof permissionToTrack !== 'undefined') {
            const { setPrivacySettings } = window?.googletag?.pubads() || {};
            if (typeof setPrivacySettings === 'function') {
                try {
                    window?.googletag?.pubads()?.setPrivacySettings({
                        limitedAds: !permissionToTrack
                    });
                } catch (error) {
                    console.log('fail to fire permissionToTrack call');
                }
            }
        }
    }, [hasAds, permissionToTrack, window?.googletag?.pubads]);

    useEffect(() => {
        // game data change
        AdsInfo.setGameData(GameData);
        if (hasAds) {
            AdsInfo.adEngine.setPageAdTargeting(AdsInfo.getKeyVals().pageTargeting);
        }
    }, [GameData, hasAds]);

    useEffect(() => {
        AdsInfo.setGameView(gameView);
        if (hasAds) {
            AdsInfo.adEngine.setPageAdTargeting(AdsInfo.getKeyVals().pageTargeting);
        }
    }, [gameView, hasAds]);

    useEffect(() => {
        AdsInfo.setGameTab(gameTab);
        if (hasAds) {
            AdsInfo.adEngine.setPageAdTargeting(AdsInfo.getKeyVals().pageTargeting);
        }
    }, [gameTab, hasAds]);

    useEffect(() => {
        if (hasAds) {
            const units = adsConfig?.units;

            if (units) {
                for (const [key, value] of Object.entries(units)) {
                    const ads = (Array.isArray(value)) ? value : [value];
                    ads.forEach((ad) => {
                        if (ad?.refresh && ad?.container) {
                            AdsInfo.adEngine.refreshAdList.push(ad?.container);
                        }
                    });
                }

                AdsInfo.adEngine.doubleclick.startRefreshTimer();
            }
        }
        return () => {
            if (hasAds) {
                AdsInfo.adEngine.doubleclick.stopRefreshTimer();
            }
        };
    }, [hasAds, adsConfig]);

    return null;
};
